import { Type } from "@sinclair/typebox";
import { IdSchema, NonNegativeIntegerSchema, Sha256Schema, TimestampSchema } from "./shared.js";
import { ReportMetadataSchema } from "./report.js";
export const ResultScalarSchema = Type.Union([
    Type.Null(),
    Type.Boolean(),
    Type.Number(),
    Type.String({ maxLength: 2_000 }),
]);
const ResultBlockTitleSchema = Type.Optional(Type.String({ minLength: 1, maxLength: 300 }));
const ResultSourcePathSchema = Type.String({ minLength: 1, maxLength: 512, pattern: "^/" });
const ResultDerivationSchema = Type.Optional(Type.Union([Type.Literal("declared"), Type.Literal("inferred"), Type.Literal("selected")]));
const ResultLinkKeySchema = Type.Union([
    Type.Number(),
    Type.String({ minLength: 1, maxLength: 256 }),
]);
export const ResultMarkdownBlockSchema = Type.Object({
    kind: Type.Literal("markdown"),
    markdown: Type.String({ minLength: 1, maxLength: 64 * 1024 }),
}, { additionalProperties: false });
export const ResultMetricsBlockSchema = Type.Object({
    kind: Type.Literal("metrics"),
    derivation: ResultDerivationSchema,
    title: ResultBlockTitleSchema,
    sourceObservationId: IdSchema,
    items: Type.Array(Type.Object({
        label: Type.String({ minLength: 1, maxLength: 200 }),
        value: ResultScalarSchema,
        unit: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
        sourcePath: ResultSourcePathSchema,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 }),
}, { additionalProperties: false });
export const ResultTableBlockSchema = Type.Object({
    kind: Type.Literal("table"),
    derivation: ResultDerivationSchema,
    title: ResultBlockTitleSchema,
    sourceObservationId: IdSchema,
    sourcePath: ResultSourcePathSchema,
    columns: Type.Array(Type.Object({
        key: IdSchema,
        label: Type.String({ minLength: 1, maxLength: 200 }),
        unit: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 12 }),
    rows: Type.Array(Type.Object({
        sourceIndex: NonNegativeIntegerSchema,
        linkKey: Type.Optional(ResultLinkKeySchema),
        cells: Type.Array(ResultScalarSchema, { minItems: 1, maxItems: 12 }),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 100 }),
    truncated: Type.Boolean(),
}, { additionalProperties: false });
const ResultChartPointSchema = Type.Object({
    sourceIndex: NonNegativeIntegerSchema,
    linkKey: Type.Optional(ResultLinkKeySchema),
    x: Type.Union([Type.Number(), Type.String({ maxLength: 256 })]),
    y: Type.Number(),
}, { additionalProperties: false });
export const ResultChartBlockSchema = Type.Object({
    kind: Type.Literal("chart"),
    derivation: ResultDerivationSchema,
    title: ResultBlockTitleSchema,
    sourceObservationId: IdSchema,
    sourcePath: ResultSourcePathSchema,
    chartType: Type.Union([Type.Literal("bar"), Type.Literal("line"), Type.Literal("scatter")]),
    xLabel: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    xUnit: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    yLabel: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    series: Type.Array(Type.Object({
        label: Type.String({ minLength: 1, maxLength: 200 }),
        unit: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
        points: Type.Array(ResultChartPointSchema, { minItems: 1, maxItems: 256 }),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 8 }),
    truncated: Type.Optional(Type.Boolean()),
}, { additionalProperties: false });
export const ResultSequenceBlockSchema = Type.Object({
    kind: Type.Literal("sequence"),
    derivation: ResultDerivationSchema,
    title: ResultBlockTitleSchema,
    sourceObservationId: IdSchema,
    sourcePath: ResultSourcePathSchema,
    alphabet: Type.Union([
        Type.Literal("rna"),
        Type.Literal("dna"),
        Type.Literal("nucleic_acid"),
        Type.Literal("protein"),
    ]),
    aligned: Type.Boolean(),
    items: Type.Array(Type.Object({
        sourceIndex: NonNegativeIntegerSchema,
        linkKey: Type.Optional(ResultLinkKeySchema),
        label: Type.String({ minLength: 1, maxLength: 200 }),
        sequence: Type.String({
            minLength: 1,
            maxLength: 4_096,
            pattern: "^[A-Z*.-]+$",
        }),
        totalLength: Type.Integer({ minimum: 1 }),
        truncated: Type.Boolean(),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 32 }),
    omittedCount: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const ResultMatrixBlockSchema = Type.Object({
    kind: Type.Literal("matrix"),
    derivation: ResultDerivationSchema,
    title: ResultBlockTitleSchema,
    sourceObservationId: IdSchema,
    sourcePath: ResultSourcePathSchema,
    columns: Type.Array(Type.Object({
        key: IdSchema,
        label: Type.String({ minLength: 1, maxLength: 200 }),
        unit: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
    }, { additionalProperties: false }), { minItems: 2, maxItems: 24 }),
    rows: Type.Array(Type.Object({
        sourceIndex: NonNegativeIntegerSchema,
        linkKey: Type.Optional(ResultLinkKeySchema),
        label: Type.String({ minLength: 1, maxLength: 200 }),
        values: Type.Array(Type.Number(), { minItems: 2, maxItems: 24 }),
    }, { additionalProperties: false }), { minItems: 2, maxItems: 48 }),
    truncated: Type.Boolean(),
}, { additionalProperties: false });
const ResultObservationStatusSchema = Type.Union([
    Type.Literal("succeeded"),
    Type.Literal("failed"),
    Type.Literal("cancelled"),
    Type.Literal("timed_out"),
]);
export const ResultTimelineBlockSchema = Type.Object({
    kind: Type.Literal("timeline"),
    title: ResultBlockTitleSchema,
    items: Type.Array(Type.Object({
        observationId: IdSchema,
        actionId: IdSchema,
        status: ResultObservationStatusSchema,
        completedAt: TimestampSchema,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 256 }),
    omittedCount: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const ResultArtifactSchema = Type.Object({
    artifactId: IdSchema,
    observationId: IdSchema,
    path: Type.String({
        minLength: 8,
        maxLength: 4_096,
        pattern: "^result/(?!\\.\\.(?:/|$))(?!.*\\/\\.\\.(?:/|$))[^\\u0000]+$",
    }),
    name: Type.String({ minLength: 1, maxLength: 255, pattern: "^[^/\\\\\\u0000]+$" }),
    mediaType: Type.String({ minLength: 1, maxLength: 255 }),
    sizeBytes: NonNegativeIntegerSchema,
    digest: Sha256Schema,
    featured: Type.Boolean(),
}, { additionalProperties: false });
export const ResultArtifactsBlockSchema = Type.Object({
    kind: Type.Literal("artifacts"),
    derivation: ResultDerivationSchema,
    title: ResultBlockTitleSchema,
    items: Type.Array(ResultArtifactSchema, { minItems: 1, maxItems: 256 }),
    groups: Type.Optional(Type.Array(Type.Object({
        title: Type.String({ minLength: 1, maxLength: 200 }),
        artifactIds: Type.Array(IdSchema, { minItems: 1, maxItems: 256 }),
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 })),
    omittedCount: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const ResultNoticeBlockSchema = Type.Object({
    kind: Type.Literal("notice"),
    level: Type.Union([Type.Literal("info"), Type.Literal("warning")]),
    code: Type.Union([
        Type.Literal("presentation_contract_mismatch"),
        Type.Literal("presentation_result_mismatch"),
    ]),
    message: Type.String({ minLength: 1, maxLength: 500 }),
    sourceObservationId: IdSchema,
    presentationId: IdSchema,
}, { additionalProperties: false });
export const ResultProvenanceBlockSchema = Type.Object({
    kind: Type.Literal("provenance"),
    basis: Type.Literal("computational"),
    sources: Type.Array(Type.Object({
        observationId: IdSchema,
        actionId: IdSchema,
        resultDigest: Sha256Schema,
        status: ResultObservationStatusSchema,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 256 }),
    omittedCount: NonNegativeIntegerSchema,
}, { additionalProperties: false });
export const ResultBlockSchema = Type.Union([
    ResultMarkdownBlockSchema,
    ResultMetricsBlockSchema,
    ResultTableBlockSchema,
    ResultChartBlockSchema,
    ResultSequenceBlockSchema,
    ResultMatrixBlockSchema,
    ResultTimelineBlockSchema,
    ResultArtifactsBlockSchema,
    ResultNoticeBlockSchema,
    ResultProvenanceBlockSchema,
    Type.Object({
        kind: Type.Literal("text"),
        title: ResultBlockTitleSchema,
        sourceObservationId: IdSchema,
        sourcePath: ResultSourcePathSchema,
        text: Type.String({ maxLength: 64 * 1024 }),
    }, { additionalProperties: false }),
]);
export const ResultDocumentSchema = Type.Object({
    kind: Type.Literal("result_document"),
    version: Type.Literal(1),
    documentId: IdSchema,
    revision: Type.Integer({ minimum: 1 }),
    title: Type.String({ minLength: 1, maxLength: 300 }),
    blocks: Type.Array(ResultBlockSchema, { minItems: 1 }),
    report: Type.Optional(ReportMetadataSchema),
}, { additionalProperties: false });
//# sourceMappingURL=result-document.js.map